import React from 'react'
import { Pressable, StyleSheet, View } from 'react-native'
import { Icon, Text, useTheme } from '@ui-kitten/components'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { isFromCustomer } from '../../../global/Support/messageHelpers'
import AttachmentChip from './AttachmentChip'
import LinkifiedText from './LinkifiedText'

export default function MessageBubble({ message, onRetry }) {
  const theme = useTheme()
  const { t } = useTranslation()
  const mine = isFromCustomer(message)
  const time = message.createdAt ? format(new Date(message.createdAt), 'HH:mm') : ''
  const isAgent = message.authorType === 'human_agent' || message.authorType === 'ai_agent'

  // System messages render as a centered, muted note rather than a bubble.
  if (message.authorType === 'system') {
    return (
      <View style={styles.systemRow}>
        <LinkifiedText
          style={[styles.systemText, { color: theme['text-hint-color'] }]}
          linkColor={theme['color-primary-500']}
        >
          {message.body}
        </LinkifiedText>
      </View>
    )
  }

  const onColor = mine ? '#fff' : theme['text-basic-color']
  const bubbleBg = mine ? theme['color-primary-500'] : theme['background-basic-color-1']

  return (
    <View style={[styles.row, mine ? styles.rowMine : styles.rowTheirs]}>
      <View style={styles.col}>
        {isAgent && message.agentName ? (
          <Text category="c2" style={[styles.agent, { color: theme['color-primary-600'] }]}>
            {message.agentName}
          </Text>
        ) : null}
        <View
          style={[
            styles.bubble,
            !mine && styles.bubbleTheirs,
            {
              backgroundColor: bubbleBg,
              borderColor: theme['border-basic-color-3'],
              borderBottomRightRadius: mine ? 6 : 18,
              borderBottomLeftRadius: mine ? 18 : 6,
              opacity: message.status === 'sending' ? 0.85 : 1,
            },
          ]}
        >
          {message.body ? (
            <LinkifiedText
              style={[styles.body, { color: onColor }]}
              linkColor={mine ? '#fff' : theme['color-primary-600']}
            >
              {message.body}
            </LinkifiedText>
          ) : null}
          {message.attachments?.length ? (
            <View style={styles.attachments}>
              {message.attachments.map((a) => (
                <AttachmentChip key={a.id} attachment={a} />
              ))}
            </View>
          ) : null}
          <View style={styles.metaRow}>
            {message.status === 'sending' ? (
              <Icon name="clock-outline" width={11} height={11} fill={mine ? 'rgba(255,255,255,0.7)' : theme['text-hint-color']} />
            ) : null}
            <Text category="c2" style={[styles.time, { color: mine ? 'rgba(255,255,255,0.7)' : theme['text-hint-color'] }]}>
              {time}
            </Text>
          </View>
        </View>
        {message.status === 'failed' ? (
          <Pressable
            onPress={() => onRetry && onRetry(message.clientId)}
            style={({ pressed }) => [styles.failedRow, pressed && { opacity: 0.6 }]}
            accessibilityRole="button"
          >
            <Icon name="refresh-outline" width={12} height={12} fill={theme['color-danger-500']} />
            <Text category="c2" status="danger" style={styles.failedText}>
              {t('support.retry')}
            </Text>
          </Pressable>
        ) : null}
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  row: { paddingHorizontal: 14, marginVertical: 3, flexDirection: 'row' },
  rowMine: { justifyContent: 'flex-end' },
  rowTheirs: { justifyContent: 'flex-start' },
  col: { maxWidth: '82%' },
  agent: { marginBottom: 3, marginLeft: 6, fontWeight: '600' },
  bubble: { borderRadius: 18, paddingHorizontal: 14, paddingVertical: 9 },
  // a hairline + subtle lift distinguishes agent bubbles from the panel surface
  bubbleTheirs: {
    borderWidth: StyleSheet.hairlineWidth,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 2,
    elevation: 1,
  },
  body: { fontSize: 15, lineHeight: 21 },
  attachments: { flexDirection: 'row', flexWrap: 'wrap' },
  metaRow: { flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-end', marginTop: 3 },
  time: { marginLeft: 4, fontSize: 11 },
  failedRow: { flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-end', marginTop: 3, marginRight: 4 },
  failedText: { marginLeft: 4 },
  systemRow: { alignItems: 'center', marginVertical: 8, paddingHorizontal: 24 },
  systemText: { textAlign: 'center', fontSize: 12 },
})
